/**
 * What a player has before calling the year (ADR-0040): the round brief (the market's move and last year's value), the left side of the one "New this round" card; the forecast sheet is its right side.
 */
import { useState } from "react";
import type { ContentCatalog, MarketView, TeamState } from "@bsim/contracts";
import { briefItems } from "../../cockpit/briefItems";
import { meBank } from "../../cockpit/derive";
import { OutlookDrawer } from "../../outlook/OutlookDrawer";
import type { KpiBridge } from "../../api/riskFields";
import { envChange, outlookLine } from "../../cockpit/derive";
import { fiscalYearLabel, formatBp, formatKpi } from "../../format";
import { AGAINST_PHRASE } from "../../format/basis";
import { GxSinceYearEnd } from "../SinceYearEnd";
import "./forecast.css";

/** The market's two rates in plain words, once: "Policy rate 3.57% (+25bp) and 10-year Treasury yield 4.40% (−10bp) at the start of FY2027". The Fed line the cockpit brief and Outlook carry is not repeated on this page. */
function ratesLine(team: TeamState, catalog: ContentCatalog, market: MarketView): string | null {
  const env = envChange(market);
  if (env === null) return null;
  const part = (label: string, now: number, bp: number | null): string => `${label} ${formatKpi("nim", now)}${bp === null || bp === 0 ? "" : ` (${formatBp(bp, { signed: true })})`}`;
  return `${part("Policy rate", env.ffRate, env.ffDeltaBp)} and ${part("10-year Treasury yield", env.tenYearRate, env.tenYearDeltaBp)} at the start of ${fiscalYearLabel(catalog, market.throughRoundNo + 1)}.`;
}

export function RoundContext({ team, catalog, market, bridge = [] }: { readonly team: TeamState; readonly catalog: ContentCatalog; readonly market: MarketView; /** The four readings' move since last year-end (`sinceLastYearEndOf`): shown here, never as a band of their own. */ readonly bridge?: readonly KpiBridge[] }) {
  const [outlookOpen, setOutlookOpen] = useState(false);
  const items = briefItems(team, market, catalog, meBank(team, market)?.score, null).filter((i) => i.key !== "waiting");
  const rates = ratesLine(team, catalog, market), outlook = outlookLine(market);
  return (
    <>
    <section className="gx-newround-facts" data-testid="decide.context">
      <h2 className="gx-h3">What changed</h2>
      <ul className="gx-newround-list">
        {items.map((it) => <li key={it.key} data-testid={`decide.context.${it.key}`}>{it.key === "changed" ? null : <span className="gx-faint">{it.title} </span>}{it.key === "changed" && rates !== null ? <>{rates}{outlook === null ? null : <span className="gx-brief-outlook">{outlook}</span>}</> : it.key === "yours" && Math.abs(meBank(team, market)?.score?.valueCreatedUsdPerShare ?? 0) >= 0.005 ? <>{it.body}<span className="gx-faint">{" "}{AGAINST_PHRASE.twin}</span></> : it.body}</li>)}
      </ul>
      <GxSinceYearEnd bridge={bridge} />
      <button type="button" className="gx-plan-inline" data-testid="decide.context.outlook" onClick={() => setOutlookOpen(true)}>Open the outlook</button>
    </section>
    {outlookOpen ? <OutlookDrawer cohortId={team.cohort.id} catalog={catalog} onClose={() => setOutlookOpen(false)} /> : null}
    </>
  );
}
